import { Smartphone } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { ExtraParamsKey, SignInIdentifier, experience } from '@logto/schemas';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LastUsedFrame } from '@/components/LastUsedChip';
import useLastUsedSignInMethod from '@/hooks/use-last-used-sign-in-method';
import { usePreserveSearchParams } from '@/hooks/use-navigate-with-preserved-search-params';

import { optionButtonClass, optionButtonProps, optionLabelClass } from './option-button-class';

type Props = {
  /** `signIn` routes to the phone sign-in first screen, `register` to phone sign-up. */
  readonly mode: 'signIn' | 'register';
  readonly className?: string;
};

/**
 * Routes to the identifier screen pre-scoped to `phone` (`?identifier=phone`), where the
 * country-code selector is always visible and the numeric keyboard opens immediately.
 */
const ContinueWithPhoneButton = ({ mode, className }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getTo } = usePreserveSearchParams();
  // The phone flow always ends in a verification code, so this is the option a remembered
  // `phone-code` sign-in points at. There is no previous sign-in to recall on register.
  const lastUsedSignInMethod = useLastUsedSignInMethod();
  const isLastUsed = mode === 'signIn' && lastUsedSignInMethod?.type === 'phone-code';

  const handlePress = useCallback(() => {
    const path =
      mode === 'signIn' ? experience.routes.identifierSignIn : experience.routes.identifierRegister;

    void navigate(
      getTo({
        pathname: `/${path}`,
        search: new URLSearchParams({
          [ExtraParamsKey.Identifier]: SignInIdentifier.Phone,
        }).toString(),
      })
    );
  }, [getTo, mode, navigate]);

  return (
    <LastUsedFrame isLastUsed={isLastUsed} className={className}>
      <Button {...optionButtonProps} className={optionButtonClass} onPress={handlePress}>
        <Smartphone className="size-5 shrink-0" />
        <span className={optionLabelClass}>
          {t('action.sign_in_with', { name: t('description.phone_number') })}
        </span>
      </Button>
    </LastUsedFrame>
  );
};

export default ContinueWithPhoneButton;
